import { cn } from '@/lib/cn';

/**
 * The one place a control's chrome is defined (§4 Forms, §4 Focus).
 *
 * `border-rule-control`, never `border-rule`: WCAG 1.4.11 requires 3:1 for the boundary of a
 * control and the decorative hairline reaches 1.2:1. An input outlined in `rule` is invisible to a
 * good many people.
 */
export const controlBase = cn(
  'rounded border border-rule-control bg-paper px-3 py-2 text-sm text-ink-900',
  'placeholder:text-ink-500 transition-motion',
  'focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent',
  'disabled:cursor-not-allowed disabled:opacity-50',
  'aria-[invalid=true]:border-negative aria-[invalid=true]:ring-negative',
);

/**
 * The focus ring shared by every non-input control (§4 Focus).
 *
 * ⚠️ **It is an *outset* ring, and that is a defect inside a scroll container** (BUG-086, noted here
 * 2026-09-28). `outline-none` plus `ring-offset-2` means the whole indicator is a `box-shadow` drawn
 * outside the border box, so a control rendered in a box with `overflow` has it clipped and looks
 * unfocused while it holds focus — BUG-081's regression, one review round, in the sidebar. §4 *Focus*
 * now carries the exception at the rule's own site: **in a scroll container, `ring-inset` replaces
 * `ring-offset-2 ring-offset-paper-bg`** and nothing else about the rule bends.
 *
 * **No inset twin is exported from here**, deliberately. It would be a second shared string with one
 * caller — `AppShell`'s navigation, which writes its own `focus-visible:ring-inset` and is asserted on
 * by `app.test.tsx` — and this project has paid twice for an export with no caller
 * (`applyTenantAccent`). The rule is what a builder needs; a second constant is what they would copy
 * without reading it.
 */
export const focusRing = cn(
  'outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2',
  'focus-visible:ring-offset-paper-bg',
);
